import { useSearchUsersQuery, type User } from '@paintscout/api'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { removeAuth0Prefix } from '@paintscout/util'
import * as Sentry from '@sentry/react'
import { useUser } from '../UserProvider'
import { useClientOptions } from '../ClientOptionsProvider'

interface UsersContextValue {
  users?: User[]
  userPictures?: Record<string, string>
  loading?: boolean
  refetchUsers?: () => void
}

export const UsersContext = createContext<UsersContextValue | undefined>(undefined)

export const UsersProvider: React.FC<{
  children: React.ReactNode
  includeCrew?: boolean
}> = ({ children, includeCrew }) => {
  const [users, setUsers] = useState<User[]>([])
  const { clientId } = useClientOptions()
  const { isSuperadmin } = useUser()
  const { user } = useUser()

  const usersQuery = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''

  const { loading, refetch } = useSearchUsersQuery({
    variables: {
      query: usersQuery,
      includeInactive: true,
      includeCrew
    },
    skip: !user,
    // cache-first (not cache-and-network): the user list rarely changes mid-session and
    // this provider wraps the whole app, so we serve the persisted IDB cache and avoid a
    // network hit on every page load. Freshness comes from refetchUsers() (called by the
    // user-mutation flows) and from management surfaces (Team/TeamNext) that run their own
    // network-only queries.
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setUsers(data?.searchUsers?.rows ?? [])
    }
  })

  // Wrap refetch so a mid-session network failure is surfaced to Sentry instead of
  // becoming an unhandled rejection that silently leaves the cached list stale.
  const refetchUsers = useCallback(
    () =>
      refetch?.().catch((err) => {
        Sentry.captureException(err)
      }),
    [refetch]
  )

  return (
    <UsersContext.Provider
      value={{
        loading,
        users,
        userPictures: useMemo(
          () =>
            users?.reduce(
              (acc, user) => {
                acc[user.user_id] =
                  user.user_metadata?.customAvatar?.cdnValue ?? user.user_metadata?.customAvatar?.value
                acc[removeAuth0Prefix(user.user_id)] =
                  user.user_metadata?.customAvatar?.cdnValue ?? user.user_metadata?.customAvatar?.value

                return acc
              },
              {} as Record<string, string>
            ),
          [users]
        ),
        refetchUsers
      }}
    >
      {children}
    </UsersContext.Provider>
  )
}

export const useUsers = (): UsersContextValue => {
  const context = useContext(UsersContext)
  if (!context) {
    throw new Error('useUsers must be used within a UsersProvider')
  }
  return context
}
